import { defineMessages } from "react-intl";

import {
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
} from "@atlassian/onboarding-survey";

export const messages = defineMessages({
    getToDoneFaster: {
        id: "PaidLanding.JiraAI.getToDoneFaster",
        defaultMessage:
            "Get to <highlight>done</highlight> faster with Jira + Rovo",
        description: "Page header"
    },
    aiPoweredProjectManagement: {
        id: "PaidLanding.JiraAI.aiPoweredProjectManagement",
        defaultMessage:
            "AI-powered project management that removes the work around work.",
        description: "Description below the page header"
    },
    softwareDevelopment: {
        id: "PaidLanding.JiraAI.madLibPrompt.softwareDevelopment",
        defaultMessage: "Software development",
        description: "Mad-lib suggestion option 1"
    },
    productManagement: {
        id: "PaidLanding.JiraAI.madLibPrompt.productManagement",
        defaultMessage: "Product management",
        description: "Mad-lib suggestion option 2"
    },
    marketing: {
        id: "PaidLanding.JiraAI.madLibPrompt.marketing",
        defaultMessage: "Marketing",
        description: "Mad-lib suggestion option 3"
    },
    projectManagement: {
        id: "PaidLanding.JiraAI.madLibPrompt.projectManagement",
        defaultMessage: "Project management",
        description: "Mad-lib suggestion option 4"
    },
    design: {
        id: "PaidLanding.JiraAI.madLibPrompt.design",
        defaultMessage: "Design",
        description: "Mad-lib suggestion option 5"
    },
    it: {
        id: "PaidLanding.JiraAI.madLibPrompt.it",
        defaultMessage: "IT",
        description: "Mad-lib suggestion option 6"
    },
    madLib: {
        id: "PaidLanding.JiraAI.madLibPrompt.madLib",
        defaultMessage:
            "<workingOn>I'm working on</workingOn> {workingOnInput} <goal>with the goal of</goal> {goalInput}",
        description:
            "Ex: I'm working on brand awareness campaign with the goal of driving 25% more signups across email, paid, and social channels"
    },
    poweredByAi: {
        id: "PaidLanding.JiraAI.madLibPrompt.poweredByAi",
        defaultMessage: "Uses AI. Verify results.",
        description: "Description in mad-lib prompt footer"
    },
    shuffle: {
        id: "PaidLanding.JiraAI.madLibPrompt.shuffle",
        defaultMessage: "Shuffle",
        description: "Button text to shuffle mad-lib"
    },
    getStarted: {
        id: "PaidLanding.JiraAI.madLibPrompt.getStarted",
        defaultMessage: "Get started",
        description: "Button text to submit mad-lib"
    },
    rerunPrompt: {
        id: "PaidLanding.JiraAI.madLibPrompt.rerunPrompt",
        defaultMessage: "Rerun prompt",
        description: "Button text to submit mad-lib"
    },
    working: {
        id: "PaidLanding.JiraAI.madLibPrompt.working",
        defaultMessage: "Working...",
        description: "Button text for loading message"
    },
    close: {
        id: "PaidLanding.JiraAI.previewModal.close",
        defaultMessage: "Close",
        description: "Label for the close button"
    },
    paidJiraAiPreviewModal: {
        id: "PaidLanding.JiraAI.signup.previewBackground.paidJiraAiPreviewModal",
        defaultMessage: "Paid Jira AI Preview Modal",
        description: "Aria label for preview modal"
    },
    acceptableUsePolicyDisclaimer: {
        id: "PaidLanding.JiraAI.acceptableUsePolicyDisclaimer",
        defaultMessage:
            'By selecting "Get started," you agree to use of a feature that is subject to Atlassian\'s <acceptableUsePolicyLink>Acceptable Use Policy</acceptableUsePolicyLink>.',
        description: "Acceptable use policy disclaimer"
    }
});

// TEAM TYPE => Software Development
export const suggestion1Message = defineMessages({
    content: {
        id: "suggestion.1.ffl.content",
        defaultMessage:
            "Feature release with the goal of meeting a key user need",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.1.ffl.task",
        defaultMessage: "feature release",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.1.ffl.goal",
        defaultMessage: "meeting a key user need",
        description: "The goal for the suggestion"
    }
});

export const suggestion2Message = defineMessages({
    content: {
        id: "suggestion.2.ffl.content",
        defaultMessage:
            "Platform migration with the goal of improving reliability",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.2.ffl.task",
        defaultMessage: "platform migration",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.2.ffl.goal",
        defaultMessage: "improving reliability",
        description: "The goal for the suggestion"
    }
});

export const suggestion3Messages = defineMessages({
    content: {
        id: "suggestion.3.ffl.content",
        defaultMessage:
            "Backend revamp with the goal of improving system performance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.3.ffl.task",
        defaultMessage: "backend revamp",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.3.ffl.goal",
        defaultMessage: "improving system performance",
        description: "The goal for the suggestion"
    }
});

export const suggestion4Messages = defineMessages({
    content: {
        id: "suggestion.4.ffl.content",
        defaultMessage:
            "Design system update with the goal of unifying components",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.4.ffl.task",
        defaultMessage: "design system update",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.4.ffl.goal",
        defaultMessage: "unifying components",
        description: "The goal for the suggestion"
    }
});

export const suggestion5Messages = defineMessages({
    content: {
        id: "suggestion.5.ffl.content",
        defaultMessage:
            "API integration project with the goal of connecting new services",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.5.ffl.task",
        defaultMessage: "api integration project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.5.ffl.goal",
        defaultMessage: "connecting new services",
        description: "The goal for the suggestion"
    }
});

export const suggestion6Messages = defineMessages({
    content: {
        id: "suggestion.6.ffl.content",
        defaultMessage:
            "Mobile app launch with the goal of delivering a responsive design",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.6.ffl.task",
        defaultMessage: "mobile app launch",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.6.ffl.goal",
        defaultMessage: "delivering a responsive design",
        description: "The goal for the suggestion"
    }
});

export const suggestion7Messages = defineMessages({
    content: {
        id: "suggestion.7.ffl.content",
        defaultMessage:
            "Security fix with the goal of resolving vulnerabilities",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.7.ffl.task",
        defaultMessage: "security fix",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.7.ffl.goal",
        defaultMessage: "resolving vulnerabilities",
        description: "The goal for the suggestion"
    }
});

export const suggestion8Messages = defineMessages({
    content: {
        id: "suggestion.8.ffl.content",
        defaultMessage:
            "Performance optimization with the goal of speeding up the app",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.8.ffl.task",
        defaultMessage: "performance optimization",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.8.ffl.goal",
        defaultMessage: "speeding up the app",
        description: "The goal for the suggestion"
    }
});

export const suggestionSoftwareDevelopment9Messages = defineMessages({
    content: {
        id: "suggestion.softwareDevelopment.9.ffl.content",
        defaultMessage:
            "Engineering sprint with the goal of QAing live prototypes built with AI",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.softwareDevelopment.9.ffl.task",
        defaultMessage: "engineering sprint",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.softwareDevelopment.9.ffl.goal",
        defaultMessage: "QAing live prototypes built with AI",
        description: "The goal for the suggestion"
    }
});

export const suggestionSoftwareDevelopment10Messages = defineMessages({
    content: {
        id: "suggestion.softwareDevelopment.10.ffl.content",
        defaultMessage:
            "Bug tracking initiative with the goal of resolving defects before feature launch",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.softwareDevelopment.10.ffl.task",
        defaultMessage: "bug tracking initiative",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.softwareDevelopment.10.ffl.goal",
        defaultMessage: "resolving defects before feature launch",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => Product Management
export const suggestion9Messages = defineMessages({
    content: {
        id: "suggestion.9.ffl.content",
        defaultMessage:
            "Product launch with the goal of shipping a new service to market",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.9.ffl.task",
        defaultMessage: "product launch",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.9.ffl.goal",
        defaultMessage: "shipping a new service to market",
        description: "The goal for the suggestion"
    }
});

export const suggestion10Messages = defineMessages({
    content: {
        id: "suggestion.10.ffl.content",
        defaultMessage:
            "Feature launch with the goal of delivering value to users",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.10.ffl.task",
        defaultMessage: "feature launch",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.10.ffl.goal",
        defaultMessage: "delivering value to users",
        description: "The goal for the suggestion"
    }
});

export const suggestion11Messages = defineMessages({
    content: {
        id: "suggestion.11.ffl.content",
        defaultMessage:
            "Strategic plan with the goal of creating a clear roadmap",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.11.ffl.task",
        defaultMessage: "strategic plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.11.ffl.goal",
        defaultMessage: "creating a clear roadmap",
        description: "The goal for the suggestion"
    }
});

export const suggestion12Messages = defineMessages({
    content: {
        id: "suggestion.12.ffl.content",
        defaultMessage:
            "Customer research study with the goal of gathering insights",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.12.ffl.task",
        defaultMessage: "customer research study",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.12.ffl.goal",
        defaultMessage: "gathering insights",
        description: "The goal for the suggestion"
    }
});

export const suggestion13Messages = defineMessages({
    content: {
        id: "suggestion.13.ffl.content",
        defaultMessage:
            "Platform upgrade with the goal of improving reliability",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.13.ffl.task",
        defaultMessage: "platform upgrade",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.13.ffl.goal",
        defaultMessage: "improving reliability",
        description: "The goal for the suggestion"
    }
});

export const suggestion14Messages = defineMessages({
    content: {
        id: "suggestion.14.ffl.content",
        defaultMessage:
            "Analytics project with the goal of tracking key metrics",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.14.ffl.task",
        defaultMessage: "analytics project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.14.ffl.goal",
        defaultMessage: "tracking key metrics",
        description: "The goal for the suggestion"
    }
});

export const suggestion15Messages = defineMessages({
    content: {
        id: "suggestion.15.ffl.content",
        defaultMessage:
            "Quarterly product plan with the goal of creating a strong backlog",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.15.ffl.task",
        defaultMessage: "quarterly product plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.15.ffl.goal",
        defaultMessage: "creating a strong backlog",
        description: "The goal for the suggestion"
    }
});

export const suggestion16Messages = defineMessages({
    content: {
        id: "suggestion.16.ffl.content",
        defaultMessage:
            "A/B experiment with the goal of optimizing an experience",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.16.ffl.task",
        defaultMessage: "a/b experiment",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.16.ffl.goal",
        defaultMessage: "optimizing an experience",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => MARKETING
export const suggestion17Messages = defineMessages({
    content: {
        id: "suggestion.17.ffl.content",
        defaultMessage:
            "Campaign launch with the goal of driving brand awareness",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.17.ffl.task",
        defaultMessage: "campaign launch",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.17.ffl.goal",
        defaultMessage: "driving brand awareness",
        description: "The goal for the suggestion"
    }
});

export const suggestion18Messages = defineMessages({
    content: {
        id: "suggestion.18.ffl.content",
        defaultMessage: "Content strategy with the goal of improving SEO",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.18.ffl.task",
        defaultMessage: "content strategy",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.18.ffl.goal",
        defaultMessage: "improving SEO",
        description: "The goal for the suggestion"
    }
});

export const suggestion19Messages = defineMessages({
    content: {
        id: "suggestion.19.ffl.content",
        defaultMessage:
            "Referral program with the goal of growing customers via referrals",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.19.ffl.task",
        defaultMessage: "referral program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.19.ffl.goal",
        defaultMessage: "growing customers via referrals",
        description: "The goal for the suggestion"
    }
});

export const suggestion20Messages = defineMessages({
    content: {
        id: "suggestion.20.ffl.content",
        defaultMessage:
            "Social media campaign with the goal of increasing engagement",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.20.ffl.task",
        defaultMessage: "social media campaign",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.20.ffl.goal",
        defaultMessage: "increasing engagement",
        description: "The goal for the suggestion"
    }
});

export const suggestion21Messages = defineMessages({
    content: {
        id: "suggestion.21.ffl.content",
        defaultMessage:
            "Influencer partnership with the goal of reaching new audiences",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.21.ffl.task",
        defaultMessage: "influencer partnership",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.21.ffl.goal",
        defaultMessage: "reaching new audiences",
        description: "The goal for the suggestion"
    }
});

export const suggestion22Messages = defineMessages({
    content: {
        id: "suggestion.22.ffl.content",
        defaultMessage:
            "Promotional event with the goal of driving brand awareness",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.22.ffl.task",
        defaultMessage: "promotional event",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.22.ffl.goal",
        defaultMessage: "driving brand awareness",
        description: "The goal for the suggestion"
    }
});

export const suggestion23Messages = defineMessages({
    content: {
        id: "suggestion.23.ffl.content",
        defaultMessage: "Email campaign with the goal of generating leads",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.23.ffl.task",
        defaultMessage: "email campaign",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.23.ffl.goal",
        defaultMessage: "generating leads",
        description: "The goal for the suggestion"
    }
});

export const suggestion24Messages = defineMessages({
    content: {
        id: "suggestion.24.ffl.content",
        defaultMessage:
            "Paid advertising with the goal of reaching new customers",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.24.ffl.task",
        defaultMessage: "paid advertising",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.24.ffl.goal",
        defaultMessage: "reaching new customers",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => OTHER
export const suggestion25Messages = defineMessages({
    content: {
        id: "suggestion.25.ffl.content",
        defaultMessage:
            "Cross-team launch with the goal of delivering a new service",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.25.ffl.task",
        defaultMessage: "cross-team launch",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.25.ffl.goal",
        defaultMessage: "delivering a new service",
        description: "The goal for the suggestion"
    }
});

export const suggestion26Messages = defineMessages({
    content: {
        id: "suggestion.26.ffl.content",
        defaultMessage: "System upgrade with the goal of improving performance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.26.ffl.task",
        defaultMessage: "system upgrade",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.26.ffl.goal",
        defaultMessage: "improving performance",
        description: "The goal for the suggestion"
    }
});

export const suggestion27Messages = defineMessages({
    content: {
        id: "suggestion.27.ffl.content",
        defaultMessage:
            "Training program with the goal of upskilling team members",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.27.ffl.task",
        defaultMessage: "training program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.27.ffl.goal",
        defaultMessage: "upskilling team members",
        description: "The goal for the suggestion"
    }
});

export const suggestion28Messages = defineMessages({
    content: {
        id: "suggestion.28.ffl.content",
        defaultMessage:
            "Communication strategy with the goal of aligning stakeholders",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.28.ffl.task",
        defaultMessage: "communication strategy",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.28.ffl.goal",
        defaultMessage: "aligning stakeholders",
        description: "The goal for the suggestion"
    }
});

export const suggestion29Messages = defineMessages({
    content: {
        id: "suggestion.29.ffl.content",
        defaultMessage:
            "Event planning initiative with the goal of engaging participants",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.29.ffl.task",
        defaultMessage: "event planning initiative",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.29.ffl.goal",
        defaultMessage: "engaging participants",
        description: "The goal for the suggestion"
    }
});

export const suggestion30Messages = defineMessages({
    content: {
        id: "suggestion.30.ffl.content",
        defaultMessage:
            "Process optimization project with the goal of increasing efficiency",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.30.ffl.task",
        defaultMessage: "process optimization project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.30.ffl.goal",
        defaultMessage: "increasing efficiency",
        description: "The goal for the suggestion"
    }
});

export const suggestion31Messages = defineMessages({
    content: {
        id: "suggestion.31.ffl.content",
        defaultMessage:
            "Company-wide initiative with the goal of improving collaboration",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.31.ffl.task",
        defaultMessage: "company-wide initiative",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.31.ffl.goal",
        defaultMessage: "improving collaboration",
        description: "The goal for the suggestion"
    }
});

export const suggestion32Messages = defineMessages({
    content: {
        id: "suggestion.32.ffl.content",
        defaultMessage:
            "Technology upgrade with the goal of modernizing our tools",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.32.ffl.task",
        defaultMessage: "technology upgrade",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.32.ffl.goal",
        defaultMessage: "modernizing our tools",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => DATA SCIENCE
export const suggestion33Messages = defineMessages({
    content: {
        id: "suggestion.33.ffl.content",
        defaultMessage: "Dashboard with the goal of tracking performance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.33.ffl.task",
        defaultMessage: "dashboard",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.33.ffl.goal",
        defaultMessage: "tracking performance",
        description: "The goal for the suggestion"
    }
});

export const suggestion34Messages = defineMessages({
    content: {
        id: "suggestion.34.ffl.content",
        defaultMessage:
            "Segmentation model with the goal of driving personalization",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.34.ffl.task",
        defaultMessage: "segmentation model",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.34.ffl.goal",
        defaultMessage: "driving personalization",
        description: "The goal for the suggestion"
    }
});

export const suggestion35Messages = defineMessages({
    content: {
        id: "suggestion.35.ffl.content",
        defaultMessage: "Predictive model with the goal of forecasting demand",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.35.ffl.task",
        defaultMessage: "predictive model",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.35.ffl.goal",
        defaultMessage: "forecasting demand",
        description: "The goal for the suggestion"
    }
});

export const suggestion36Messages = defineMessages({
    content: {
        id: "suggestion.36.ffl.content",
        defaultMessage:
            "A/B test analysis with the goal of measuring experiment impact",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.36.ffl.task",
        defaultMessage: "a/b test analysis",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.36.ffl.goal",
        defaultMessage: "measuring experiment impact",
        description: "The goal for the suggestion"
    }
});

export const suggestion37Messages = defineMessages({
    content: {
        id: "suggestion.37.ffl.content",
        defaultMessage:
            "Churn analysis with the goal of reducing customer loss",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.37.ffl.task",
        defaultMessage: "churn analysis",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.37.ffl.goal",
        defaultMessage: "reducing customer loss",
        description: "The goal for the suggestion"
    }
});

export const suggestion38Messages = defineMessages({
    content: {
        id: "suggestion.38.ffl.content",
        defaultMessage: "Data pipeline with the goal of improving data quality",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.38.ffl.task",
        defaultMessage: "data pipeline",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.38.ffl.goal",
        defaultMessage: "improving data quality",
        description: "The goal for the suggestion"
    }
});

export const suggestion39Messages = defineMessages({
    content: {
        id: "suggestion.39.ffl.content",
        defaultMessage:
            "Data lake migration with the goal of centralizing datasets",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.39.ffl.task",
        defaultMessage: "data lake migration",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.39.ffl.goal",
        defaultMessage: "centralizing datasets",
        description: "The goal for the suggestion"
    }
});

export const suggestion40Messages = defineMessages({
    content: {
        id: "suggestion.40.ffl.content",
        defaultMessage:
            "Funnel analysis with the goal of identifying new opportunities",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.40.ffl.task",
        defaultMessage: "funnel analysis",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.40.ffl.goal",
        defaultMessage: "identifying new opportunities",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => SALES
export const suggestion41Messages = defineMessages({
    content: {
        id: "suggestion.41.ffl.content",
        defaultMessage:
            "Lead generation project with the goal of finding new customers",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.41.ffl.task",
        defaultMessage: "lead generation project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.41.ffl.goal",
        defaultMessage: "finding new customers",
        description: "The goal for the suggestion"
    }
});

export const suggestion42Messages = defineMessages({
    content: {
        id: "suggestion.42.ffl.content",
        defaultMessage: "Sales campaign with the goal of increasing revenue",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.42.ffl.task",
        defaultMessage: "sales campaign",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.42.ffl.goal",
        defaultMessage: "increasing revenue",
        description: "The goal for the suggestion"
    }
});

export const suggestion43Messages = defineMessages({
    content: {
        id: "suggestion.43.ffl.content",
        defaultMessage:
            "Account plan with the goal of growing client relationships",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.43.ffl.task",
        defaultMessage: "account plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.43.ffl.goal",
        defaultMessage: "growing client relationships",
        description: "The goal for the suggestion"
    }
});

export const suggestion44Messages = defineMessages({
    content: {
        id: "suggestion.44.ffl.content",
        defaultMessage:
            "Sales kickoff plan with the goal of driving sales growth",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.44.ffl.task",
        defaultMessage: "sales kickoff plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.44.ffl.goal",
        defaultMessage: "driving sales growth",
        description: "The goal for the suggestion"
    }
});

export const suggestion45Messages = defineMessages({
    content: {
        id: "suggestion.45.ffl.content",
        defaultMessage:
            "Pipeline optimization with the goal of increasing deal flow",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.45.ffl.task",
        defaultMessage: "pipeline optimization",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.45.ffl.goal",
        defaultMessage: "increasing deal flow",
        description: "The goal for the suggestion"
    }
});

export const suggestion46Messages = defineMessages({
    content: {
        id: "suggestion.46.ffl.content",
        defaultMessage:
            "Sales training program with the goal of improving team skills",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.46.ffl.task",
        defaultMessage: "sales training program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.46.ffl.goal",
        defaultMessage: "improving team skills",
        description: "The goal for the suggestion"
    }
});

export const suggestion47Messages = defineMessages({
    content: {
        id: "suggestion.47.ffl.content",
        defaultMessage:
            "Market analysis with the goal of identifying new opportunities",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.47.ffl.task",
        defaultMessage: "market analysis",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.47.ffl.goal",
        defaultMessage: "identifying new opportunities",
        description: "The goal for the suggestion"
    }
});

export const suggestion48Messages = defineMessages({
    content: {
        id: "suggestion.48.ffl.content",
        defaultMessage:
            "Customer retention initiative with the goal of reducing churn",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.48.ffl.task",
        defaultMessage: "customer retention initiative",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.48.ffl.goal",
        defaultMessage: "reducing churn",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => FINANCE
export const suggestion49Messages = defineMessages({
    content: {
        id: "suggestion.49.ffl.content",
        defaultMessage:
            "Annual budget plan with the goal of optimizing resource allocation",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.49.ffl.task",
        defaultMessage: "annual budget plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.49.ffl.goal",
        defaultMessage: "optimizing resource allocation",
        description: "The goal for the suggestion"
    }
});

export const suggestion50Messages = defineMessages({
    content: {
        id: "suggestion.50.ffl.content",
        defaultMessage:
            "Financial dashboard with the goal of surfacing key metrics",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.50.ffl.task",
        defaultMessage: "financial dashboard",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.50.ffl.goal",
        defaultMessage: "surfacing key metrics",
        description: "The goal for the suggestion"
    }
});

export const suggestion51Messages = defineMessages({
    content: {
        id: "suggestion.51.ffl.content",
        defaultMessage:
            "Financial audit preparation with the goal of ensuring compliance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.51.ffl.task",
        defaultMessage: "financial audit preparation",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.51.ffl.goal",
        defaultMessage: "ensuring compliance",
        description: "The goal for the suggestion"
    }
});

export const suggestion52Messages = defineMessages({
    content: {
        id: "suggestion.52.ffl.content",
        defaultMessage:
            "Cost reduction project with the goal of improving profitability",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.52.ffl.task",
        defaultMessage: "cost reduction project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.52.ffl.goal",
        defaultMessage: "improving profitability",
        description: "The goal for the suggestion"
    }
});

export const suggestion53Messages = defineMessages({
    content: {
        id: "suggestion.53.ffl.content",
        defaultMessage:
            "Forecasting project with the goal of predicting revenue",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.53.ffl.task",
        defaultMessage: "forecasting project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.53.ffl.goal",
        defaultMessage: "predicting revenue",
        description: "The goal for the suggestion"
    }
});

export const suggestion54Messages = defineMessages({
    content: {
        id: "suggestion.54.ffl.content",
        defaultMessage:
            "Automation project with the goal of streamlining operations",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.54.ffl.task",
        defaultMessage: "automation project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.54.ffl.goal",
        defaultMessage: "streamlining operations",
        description: "The goal for the suggestion"
    }
});

export const suggestion55Messages = defineMessages({
    content: {
        id: "suggestion.55.ffl.content",
        defaultMessage:
            "Technology upgrade with the goal of modernizing our systems",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.55.ffl.task",
        defaultMessage: "technology upgrade",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.55.ffl.goal",
        defaultMessage: "modernizing our systems",
        description: "The goal for the suggestion"
    }
});

export const suggestion56Messages = defineMessages({
    content: {
        id: "suggestion.56.ffl.content",
        defaultMessage:
            "Tax planning initiative with the goal of ensuring compliance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.56.ffl.task",
        defaultMessage: "tax planning initiative",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.56.ffl.goal",
        defaultMessage: "ensuring compliance",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => LEGAL
export const suggestion57Messages = defineMessages({
    content: {
        id: "suggestion.57.ffl.content",
        defaultMessage:
            "Contract review project with the goal of faster approvals",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.57.ffl.task",
        defaultMessage: "contract review project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.57.ffl.goal",
        defaultMessage: "faster approvals",
        description: "The goal for the suggestion"
    }
});

export const suggestion58Messages = defineMessages({
    content: {
        id: "suggestion.58.ffl.content",
        defaultMessage:
            "Compliance rollout with the goal of meeting regulatory standards",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.58.ffl.task",
        defaultMessage: "compliance rollout",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.58.ffl.goal",
        defaultMessage: "meeting regulatory standards",
        description: "The goal for the suggestion"
    }
});

export const suggestion59Messages = defineMessages({
    content: {
        id: "suggestion.59.ffl.content",
        defaultMessage:
            "Property transaction with the goal of finalizing a deal",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.59.ffl.task",
        defaultMessage: "property transaction",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.59.ffl.goal",
        defaultMessage: "finalizing a deal",
        description: "The goal for the suggestion"
    }
});

export const suggestion60Messages = defineMessages({
    content: {
        id: "suggestion.60.ffl.content",
        defaultMessage:
            "IP litigation case with the goal of protecting intellectual property",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.60.ffl.task",
        defaultMessage: "ip litigation case",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.60.ffl.goal",
        defaultMessage: "protecting intellectual property",
        description: "The goal for the suggestion"
    }
});

export const suggestion61Messages = defineMessages({
    content: {
        id: "suggestion.61.ffl.content",
        defaultMessage:
            "Legal intake portal with the goal of streamlining request handling",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.61.ffl.task",
        defaultMessage: "legal intake portal",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.61.ffl.goal",
        defaultMessage: "streamlining request handling",
        description: "The goal for the suggestion"
    }
});

export const suggestion62Messages = defineMessages({
    content: {
        id: "suggestion.62.ffl.content",
        defaultMessage:
            "Data privacy project with the goal of protecting customer data",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.62.ffl.task",
        defaultMessage: "data privacy project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.62.ffl.goal",
        defaultMessage: "protecting customer data",
        description: "The goal for the suggestion"
    }
});

export const suggestion63Messages = defineMessages({
    content: {
        id: "suggestion.63.ffl.content",
        defaultMessage:
            "Employee policy review with the goal of ensuring compliance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.63.ffl.task",
        defaultMessage: "employee policy review",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.63.ffl.goal",
        defaultMessage: "ensuring compliance",
        description: "The goal for the suggestion"
    }
});

export const suggestion64Messages = defineMessages({
    content: {
        id: "suggestion.64.ffl.content",
        defaultMessage:
            "Legal tech adoption plan with the goal of improving team efficiency",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.64.ffl.task",
        defaultMessage: "legal tech adoption plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.64.ffl.goal",
        defaultMessage: "improving team efficiency",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => CUSTOMER SERVICE
export const suggestion65Messages = defineMessages({
    content: {
        id: "suggestion.65.ffl.content",
        defaultMessage: "Support project with the goal of monitoring requests",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.65.ffl.task",
        defaultMessage: "support project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.65.ffl.goal",
        defaultMessage: "monitoring requests",
        description: "The goal for the suggestion"
    }
});

export const suggestion66Messages = defineMessages({
    content: {
        id: "suggestion.66.ffl.content",
        defaultMessage:
            "Knowledge base redesign with the goal of simplifying resources",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.66.ffl.task",
        defaultMessage: "knowledge base redesign",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.66.ffl.goal",
        defaultMessage: "simplifying resources",
        description: "The goal for the suggestion"
    }
});

export const suggestion67Messages = defineMessages({
    content: {
        id: "suggestion.67.ffl.content",
        defaultMessage: "AI chat tool with the goal of automating responses",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.67.ffl.task",
        defaultMessage: "ai chat tool",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.67.ffl.goal",
        defaultMessage: "automating responses",
        description: "The goal for the suggestion"
    }
});

export const suggestion68Messages = defineMessages({
    content: {
        id: "suggestion.68.ffl.content",
        defaultMessage:
            "Customer feedback survey with the goal of gathering insights",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.68.ffl.task",
        defaultMessage: "customer feedback survey",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.68.ffl.goal",
        defaultMessage: "gathering insights",
        description: "The goal for the suggestion"
    }
});

export const suggestion69Messages = defineMessages({
    content: {
        id: "suggestion.69.ffl.content",
        defaultMessage:
            "Process optimization with the goal of reducing bottlenecks",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.69.ffl.task",
        defaultMessage: "process optimization",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.69.ffl.goal",
        defaultMessage: "reducing bottlenecks",
        description: "The goal for the suggestion"
    }
});

export const suggestion70Messages = defineMessages({
    content: {
        id: "suggestion.70.ffl.content",
        defaultMessage:
            "Multi-channel integration with the goal of centralizing interactions",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.70.ffl.task",
        defaultMessage: "multi-channel integration",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.70.ffl.goal",
        defaultMessage: "centralizing interactions",
        description: "The goal for the suggestion"
    }
});

export const suggestion71Messages = defineMessages({
    content: {
        id: "suggestion.71.ffl.content",
        defaultMessage:
            "Support dashboard with the goal of tracking key metrics",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.71.ffl.task",
        defaultMessage: "support dashboard",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.71.ffl.goal",
        defaultMessage: "tracking key metrics",
        description: "The goal for the suggestion"
    }
});

export const suggestion72Messages = defineMessages({
    content: {
        id: "suggestion.72.ffl.content",
        defaultMessage:
            "Training program with the goal of improving team skills",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.72.ffl.task",
        defaultMessage: "training program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.72.ffl.goal",
        defaultMessage: "improving team skills",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => HUMAN RESOURCES
export const suggestion73Messages = defineMessages({
    content: {
        id: "suggestion.73.ffl.content",
        defaultMessage:
            "New hire onboarding with the goal of ramping up employees",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.73.ffl.task",
        defaultMessage: "new hire onboarding",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.73.ffl.goal",
        defaultMessage: "ramping up employees",
        description: "The goal for the suggestion"
    }
});

export const suggestion74Messages = defineMessages({
    content: {
        id: "suggestion.74.ffl.content",
        defaultMessage: "Employee training with the goal of building skills",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.74.ffl.task",
        defaultMessage: "employee training",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.74.ffl.goal",
        defaultMessage: "building skills",
        description: "The goal for the suggestion"
    }
});

export const suggestion75Messages = defineMessages({
    content: {
        id: "suggestion.75.ffl.content",
        defaultMessage:
            "Performance review process with the goal of evaluating growth",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.75.ffl.task",
        defaultMessage: "performance review process",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.75.ffl.goal",
        defaultMessage: "evaluating growth",
        description: "The goal for the suggestion"
    }
});

export const suggestion76Messages = defineMessages({
    content: {
        id: "suggestion.76.ffl.content",
        defaultMessage:
            "Benefits rollout with the goal of improving employee satisfaction",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.76.ffl.task",
        defaultMessage: "benefits rollout",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.76.ffl.goal",
        defaultMessage: "improving employee satisfaction",
        description: "The goal for the suggestion"
    }
});

export const suggestion77Messages = defineMessages({
    content: {
        id: "suggestion.77.ffl.content",
        defaultMessage:
            "Recruitment campaign with the goal of hiring top talent",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.77.ffl.task",
        defaultMessage: "recruitment campaign",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.77.ffl.goal",
        defaultMessage: "hiring top talent",
        description: "The goal for the suggestion"
    }
});

export const suggestion78Messages = defineMessages({
    content: {
        id: "suggestion.78.ffl.content",
        defaultMessage: "Employee survey with the goal of gathering feedback",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.78.ffl.task",
        defaultMessage: "employee survey",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.78.ffl.goal",
        defaultMessage: "gathering feedback",
        description: "The goal for the suggestion"
    }
});

export const suggestion79Messages = defineMessages({
    content: {
        id: "suggestion.79.ffl.content",
        defaultMessage: "Compliance audit with the goal of meeting regulations",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.79.ffl.task",
        defaultMessage: "compliance audit",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.79.ffl.goal",
        defaultMessage: "meeting regulations",
        description: "The goal for the suggestion"
    }
});

export const suggestion80Messages = defineMessages({
    content: {
        id: "suggestion.80.ffl.content",
        defaultMessage:
            "Campus hiring program with the goal of recruiting new graduates",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.80.ffl.task",
        defaultMessage: "campus hiring program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.80.ffl.goal",
        defaultMessage: "recruiting new graduates",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => IT SUPPORT
export const suggestion81Messages = defineMessages({
    content: {
        id: "suggestion.81.ffl.content",
        defaultMessage:
            "Knowledge base with the goal of speeding up troubleshooting",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.81.ffl.task",
        defaultMessage: "knowledge base",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.81.ffl.goal",
        defaultMessage: "speeding up troubleshooting",
        description: "The goal for the suggestion"
    }
});

export const suggestion82Messages = defineMessages({
    content: {
        id: "suggestion.82.ffl.content",
        defaultMessage: "Support workflow with the goal of managing requests",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.82.ffl.task",
        defaultMessage: "support workflow",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.82.ffl.goal",
        defaultMessage: "managing requests",
        description: "The goal for the suggestion"
    }
});

export const suggestion83Messages = defineMessages({
    content: {
        id: "suggestion.83.ffl.content",
        defaultMessage:
            "Asset management project with the goal of tracking hardware",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.83.ffl.task",
        defaultMessage: "asset management project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.83.ffl.goal",
        defaultMessage: "tracking hardware",
        description: "The goal for the suggestion"
    }
});

export const suggestion84Messages = defineMessages({
    content: {
        id: "suggestion.84.ffl.content",
        defaultMessage:
            "Backup and recovery plan with the goal of protecting data",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.84.ffl.task",
        defaultMessage: "backup and recovery plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.84.ffl.goal",
        defaultMessage: "protecting data",
        description: "The goal for the suggestion"
    }
});

export const suggestion85Messages = defineMessages({
    content: {
        id: "suggestion.85.ffl.content",
        defaultMessage:
            "Training program with the goal of reducing support requests",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.85.ffl.task",
        defaultMessage: "training program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.85.ffl.goal",
        defaultMessage: "reducing support requests",
        description: "The goal for the suggestion"
    }
});

export const suggestion86Messages = defineMessages({
    content: {
        id: "suggestion.86.ffl.content",
        defaultMessage:
            "Vendor procurement project with the goal of managing suppliers",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.86.ffl.task",
        defaultMessage: "vendor procurement project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.86.ffl.goal",
        defaultMessage: "managing suppliers",
        description: "The goal for the suggestion"
    }
});

export const suggestion87Messages = defineMessages({
    content: {
        id: "suggestion.87.ffl.content",
        defaultMessage: "System upgrade with the goal of improving reliability",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.87.ffl.task",
        defaultMessage: "system upgrade",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.87.ffl.goal",
        defaultMessage: "improving reliability",
        description: "The goal for the suggestion"
    }
});

export const suggestion88Messages = defineMessages({
    content: {
        id: "suggestion.88.ffl.content",
        defaultMessage:
            "Dashboard with the goal of tracking system performance",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.88.ffl.task",
        defaultMessage: "dashboard",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.88.ffl.goal",
        defaultMessage: "tracking system performance",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => OPERATIONS
export const suggestion89Messages = defineMessages({
    content: {
        id: "suggestion.89.ffl.content",
        defaultMessage:
            "Process update initiative with the goal of optimizing workflows",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.89.ffl.task",
        defaultMessage: "process update initiative",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.89.ffl.goal",
        defaultMessage: "optimizing workflows",
        description: "The goal for the suggestion"
    }
});

export const suggestion90Messages = defineMessages({
    content: {
        id: "suggestion.90.ffl.content",
        defaultMessage:
            "Dashboard with the goal of providing actionable insights",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.90.ffl.task",
        defaultMessage: "dashboard",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.90.ffl.goal",
        defaultMessage: "providing actionable insights",
        description: "The goal for the suggestion"
    }
});

export const suggestion91Messages = defineMessages({
    content: {
        id: "suggestion.91.ffl.content",
        defaultMessage:
            "Inventory overhaul with the goal of improving stock management",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.91.ffl.task",
        defaultMessage: "inventory overhaul",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.91.ffl.goal",
        defaultMessage: "improving stock management",
        description: "The goal for the suggestion"
    }
});

export const suggestion92Messages = defineMessages({
    content: {
        id: "suggestion.92.ffl.content",
        defaultMessage:
            "Workflow update with the goal of improving team collaboration",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.92.ffl.task",
        defaultMessage: "workflow update",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.92.ffl.goal",
        defaultMessage: "improving team collaboration",
        description: "The goal for the suggestion"
    }
});

export const suggestion93Messages = defineMessages({
    content: {
        id: "suggestion.93.ffl.content",
        defaultMessage:
            "Vendor onboarding program with the goal of streamlining suppliers",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.93.ffl.task",
        defaultMessage: "vendor onboarding program",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.93.ffl.goal",
        defaultMessage: "streamlining suppliers",
        description: "The goal for the suggestion"
    }
});

export const suggestion94Messages = defineMessages({
    content: {
        id: "suggestion.94.ffl.content",
        defaultMessage:
            "Logistics plan with the goal of ensuring smooth operations",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.94.ffl.task",
        defaultMessage: "logistics plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.94.ffl.goal",
        defaultMessage: "ensuring smooth operations",
        description: "The goal for the suggestion"
    }
});

export const suggestion95Messages = defineMessages({
    content: {
        id: "suggestion.95.ffl.content",
        defaultMessage: "Budget plan with the goal of controlling costs",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.95.ffl.task",
        defaultMessage: "budget plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.95.ffl.goal",
        defaultMessage: "controlling costs",
        description: "The goal for the suggestion"
    }
});

export const suggestion96Messages = defineMessages({
    content: {
        id: "suggestion.96.ffl.content",
        defaultMessage:
            "Process optimization project with the goal of reducing bottlenecks",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.96.ffl.task",
        defaultMessage: "process optimization project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.96.ffl.goal",
        defaultMessage: "reducing bottlenecks",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => DESIGN
// TEAM TYPE => DESIGN
export const suggestion97Messages = defineMessages({
    content: {
        id: "suggestion.97.ffl.content",
        defaultMessage:
            "Product feature with the goal of improving the user experience",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.97.ffl.task",
        defaultMessage: "product feature",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.97.ffl.goal",
        defaultMessage: "improving the user experience",
        description: "The goal for the suggestion"
    }
});

export const suggestion98Messages = defineMessages({
    content: {
        id: "suggestion.98.ffl.content",
        defaultMessage:
            "Design system with the goal of scaling consistent components",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.98.ffl.task",
        defaultMessage: "design system",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.98.ffl.goal",
        defaultMessage: "scaling consistent components",
        description: "The goal for the suggestion"
    }
});

export const suggestion99Messages = defineMessages({
    content: {
        id: "suggestion.99.ffl.content",
        defaultMessage:
            "Website redesign with the goal of simplifying navigation",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.99.ffl.task",
        defaultMessage: "website redesign",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.99.ffl.goal",
        defaultMessage: "simplifying navigation",
        description: "The goal for the suggestion"
    }
});

export const suggestion100Messages = defineMessages({
    content: {
        id: "suggestion.100.ffl.content",
        defaultMessage: "Brand refresh with the goal of unifying visuals",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.100.ffl.task",
        defaultMessage: "brand refresh",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.100.ffl.goal",
        defaultMessage: "unifying visuals",
        description: "The goal for the suggestion"
    }
});

export const suggestion101Messages = defineMessages({
    content: {
        id: "suggestion.101.ffl.content",
        defaultMessage:
            "Interactive prototype with the goal of testing new concepts",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.101.ffl.task",
        defaultMessage: "interactive prototype",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.101.ffl.goal",
        defaultMessage: "testing new concepts",
        description: "The goal for the suggestion"
    }
});

export const suggestion102Messages = defineMessages({
    content: {
        id: "suggestion.102.ffl.content",
        defaultMessage:
            "Research study with the goal of validating design decisions",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.102.ffl.task",
        defaultMessage: "research study",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.102.ffl.goal",
        defaultMessage: "validating design decisions",
        description: "The goal for the suggestion"
    }
});

export const suggestion103Messages = defineMessages({
    content: {
        id: "suggestion.103.ffl.content",
        defaultMessage:
            "Content strategy with the goal of creating a consistent voice",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.103.ffl.task",
        defaultMessage: "content strategy",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.103.ffl.goal",
        defaultMessage: "creating a consistent voice",
        description: "The goal for the suggestion"
    }
});

export const suggestion104Messages = defineMessages({
    content: {
        id: "suggestion.104.ffl.content",
        defaultMessage:
            "Mobile app design with the goal of streamlining core user flows",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.104.ffl.task",
        defaultMessage: "mobile app design",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.104.ffl.goal",
        defaultMessage: "streamlining core user flows",
        description: "The goal for the suggestion"
    }
});

// TEAM TYPE => PROJECT MANAGEMENT
export const suggestion105Messages = defineMessages({
    content: {
        id: "suggestion.105.ffl.content",
        defaultMessage:
            "Product release with the goal of keeping teams aligned",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.105.ffl.task",
        defaultMessage: "product release",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.105.ffl.goal",
        defaultMessage: "keeping teams aligned",
        description: "The goal for the suggestion"
    }
});

export const suggestion106Messages = defineMessages({
    content: {
        id: "suggestion.106.ffl.content",
        defaultMessage: "A new product with the goal of increasing revenue",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.106.ffl.task",
        defaultMessage: "a new product",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.106.ffl.goal",
        defaultMessage: "increasing revenue",
        description: "The goal for the suggestion"
    }
});

export const suggestion107Messages = defineMessages({
    content: {
        id: "suggestion.107.ffl.content",
        defaultMessage:
            "Marketing launch with the goal of ensuring on-time delivery",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.107.ffl.task",
        defaultMessage: "marketing launch",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.107.ffl.goal",
        defaultMessage: "ensuring on-time delivery",
        description: "The goal for the suggestion"
    }
});

export const suggestion108Messages = defineMessages({
    content: {
        id: "suggestion.108.ffl.content",
        defaultMessage: "Resource plan with the goal of balancing workloads",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.108.ffl.task",
        defaultMessage: "resource plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.108.ffl.goal",
        defaultMessage: "balancing workloads",
        description: "The goal for the suggestion"
    }
});

export const suggestion109Messages = defineMessages({
    content: {
        id: "suggestion.109.ffl.content",
        defaultMessage:
            "Status report with the goal of keeping leadership informed",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.109.ffl.task",
        defaultMessage: "status report",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.109.ffl.goal",
        defaultMessage: "keeping leadership informed",
        description: "The goal for the suggestion"
    }
});

export const suggestion110Messages = defineMessages({
    content: {
        id: "suggestion.110.ffl.content",
        defaultMessage: "Timeline plan with the goal of meeting deadlines",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.110.ffl.task",
        defaultMessage: "timeline plan",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.110.ffl.goal",
        defaultMessage: "meeting deadlines",
        description: "The goal for the suggestion"
    }
});

export const suggestion111Messages = defineMessages({
    content: {
        id: "suggestion.111.ffl.content",
        defaultMessage:
            "Risk assessment with the goal of reducing project blockers",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.111.ffl.task",
        defaultMessage: "risk assessment",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.111.ffl.goal",
        defaultMessage: "reducing project blockers",
        description: "The goal for the suggestion"
    }
});

export const suggestion112Messages = defineMessages({
    content: {
        id: "suggestion.112.ffl.content",
        defaultMessage:
            "Cross-functional project with the goal of coordinating team tasks",
        description: "The content for the suggestion"
    },
    task: {
        id: "suggestion.112.ffl.task",
        defaultMessage: "cross-functional project",
        description: "The task for the suggestion"
    },
    goal: {
        id: "suggestion.112.ffl.goal",
        defaultMessage: "coordinating team tasks",
        description: "The goal for the suggestion"
    }
});

export const getSuggestionsForTeamType = (teamType: string) => {
    switch (teamType) {
        case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
            return [
                suggestion1Message,
                suggestion2Message,
                suggestion3Messages,
                suggestion4Messages,
                suggestion5Messages,
                suggestion6Messages,
                suggestion7Messages,
                suggestion8Messages,
                suggestionSoftwareDevelopment9Messages,
                suggestionSoftwareDevelopment10Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
            return [
                suggestion9Messages,
                suggestion10Messages,
                suggestion11Messages,
                suggestion12Messages,
                suggestion13Messages,
                suggestion14Messages,
                suggestion15Messages,
                suggestion16Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
            return [
                suggestion17Messages,
                suggestion18Messages,
                suggestion19Messages,
                suggestion20Messages,
                suggestion21Messages,
                suggestion22Messages,
                suggestion23Messages,
                suggestion24Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL:
            return [
                suggestion25Messages,
                suggestion26Messages,
                suggestion27Messages,
                suggestion28Messages,
                suggestion29Messages,
                suggestion30Messages,
                suggestion31Messages,
                suggestion32Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE:
            return [
                suggestion33Messages,
                suggestion34Messages,
                suggestion35Messages,
                suggestion36Messages,
                suggestion37Messages,
                suggestion38Messages,
                suggestion39Messages,
                suggestion40Messages
            ];

        case USER_SEGMENTATION_TEAM_TYPE_SALES:
            return [
                suggestion41Messages,
                suggestion42Messages,
                suggestion43Messages,
                suggestion44Messages,
                suggestion45Messages,
                suggestion46Messages,
                suggestion47Messages,
                suggestion48Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_FINANCE:
            return [
                suggestion49Messages,
                suggestion50Messages,
                suggestion51Messages,
                suggestion52Messages,
                suggestion53Messages,
                suggestion54Messages,
                suggestion55Messages,
                suggestion56Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_LEGAL:
            return [
                suggestion57Messages,
                suggestion58Messages,
                suggestion59Messages,
                suggestion60Messages,
                suggestion61Messages,
                suggestion62Messages,
                suggestion63Messages,
                suggestion64Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE:
            return [
                suggestion65Messages,
                suggestion66Messages,
                suggestion67Messages,
                suggestion68Messages,
                suggestion69Messages,
                suggestion70Messages,
                suggestion71Messages,
                suggestion72Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_HR:
            return [
                suggestion73Messages,
                suggestion74Messages,
                suggestion75Messages,
                suggestion76Messages,
                suggestion77Messages,
                suggestion78Messages,
                suggestion79Messages,
                suggestion80Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT:
            return [
                suggestion81Messages,
                suggestion82Messages,
                suggestion83Messages,
                suggestion84Messages,
                suggestion85Messages,
                suggestion86Messages,
                suggestion87Messages,
                suggestion88Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_OPERATIONS:
            return [
                suggestion89Messages,
                suggestion90Messages,
                suggestion91Messages,
                suggestion92Messages,
                suggestion93Messages,
                suggestion94Messages,
                suggestion95Messages,
                suggestion96Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
            return [
                suggestion97Messages,
                suggestion98Messages,
                suggestion99Messages,
                suggestion100Messages,
                suggestion101Messages,
                suggestion102Messages,
                suggestion103Messages,
                suggestion104Messages
            ];
        case USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT:
            return [
                suggestion105Messages,
                suggestion106Messages,
                suggestion107Messages,
                suggestion108Messages,
                suggestion109Messages,
                suggestion110Messages,
                suggestion111Messages,
                suggestion112Messages
            ];
        default:
            return [];
    }
};
